import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    FormLabel,
    OutlinedInput,
    Stack,
    Typography,
} from "@mui/material";
import api from "../../../utils/api";

const initialForm = {
    id: null,
    name: "",
    description: "",
    max_value: "100",
};

export default function CourseAssignmentsModal({ open, onClose, course }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const isEdit = useMemo(() => !!form.id, [form.id]);

    const resetForm = () => {
        setForm(initialForm);
        setErrors({});
    };

    const loadRows = async () => {
        if (!course?.id) return;
        setLoading(true);
        try {
            const res = await api.get(`admin/courses/${course.id}/assignments`);
            setRows(res.data?.data || []);
        } catch (e) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        resetForm();
        loadRows();
    }, [open, course?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async () => {
        if (!course?.id) return;
        setSaving(true);
        setErrors({});
        const payload = {
            name: form.name,
            description: form.description || null,
            max_value: Number(form.max_value),
        };
        try {
            if (isEdit) {
                await api.put(`admin/courses/${course.id}/assignments/${form.id}`, payload);
            } else {
                await api.post(`admin/courses/${course.id}/assignments`, payload);
            }
            await loadRows();
            resetForm();
        } catch (e) {
            if (e?.errors) setErrors(e.errors);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!course?.id) return;
        if (!window.confirm("Delete this assignment?")) return;
        try {
            await api.delete(`admin/courses/${course.id}/assignments/${id}`);
            await loadRows();
            if (form.id === id) resetForm();
        } catch (e) {}
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                onClose();
            }}
            disableEscapeKeyDown
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Assignments {course ? `- ${course.name_en || course.name_ru || course.name_hy || `Course #${course.id}`}` : ""}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                            {isEdit ? "Edit assignment" : "Add assignment"}
                        </Typography>
                        <Stack spacing={1.5}>
                            <FormControl error={!!errors.name?.[0]}>
                                <FormLabel>Name</FormLabel>
                                <OutlinedInput
                                    value={form.name}
                                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                                />
                                {errors.name ? <FormHelperText>{errors.name[0]}</FormHelperText> : null}
                            </FormControl>
                            <FormControl error={!!errors.description?.[0]}>
                                <FormLabel>Description</FormLabel>
                                <OutlinedInput
                                    multiline
                                    minRows={3}
                                    value={form.description}
                                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                                />
                                {errors.description ? <FormHelperText>{errors.description[0]}</FormHelperText> : null}
                            </FormControl>
                            <FormControl error={!!errors.max_value?.[0]}>
                                <FormLabel>Max value</FormLabel>
                                <OutlinedInput
                                    type="number"
                                    inputProps={{ min: 0.01, step: 0.01 }}
                                    value={form.max_value}
                                    onChange={(e) => setForm((prev) => ({ ...prev, max_value: e.target.value }))}
                                />
                                {errors.max_value ? <FormHelperText>{errors.max_value[0]}</FormHelperText> : null}
                            </FormControl>
                            <Stack direction="row" spacing={1}>
                                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                                    {isEdit ? "Update" : "Add"}
                                </Button>
                                {isEdit ? (
                                    <Button variant="outlined" onClick={resetForm}>
                                        Cancel edit
                                    </Button>
                                ) : null}
                            </Stack>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Existing assignments
                        </Typography>
                        {loading ? (
                            <Typography color="text.secondary">Loading...</Typography>
                        ) : rows.length === 0 ? (
                            <Alert severity="info">No assignments yet.</Alert>
                        ) : (
                            <Stack spacing={1}>
                                {rows.map((item) => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            p: 1.5,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            gap: 2,
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Max: {item.max_value}
                                            </Typography>
                                            {item.description ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                                                    {item.description}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() =>
                                                    setForm({
                                                        id: item.id,
                                                        name: item.name || "",
                                                        description: item.description || "",
                                                        max_value: String(item.max_value ?? ""),
                                                    })
                                                }
                                            >
                                                Edit
                                            </Button>
                                            <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(item.id)}>
                                                Delete
                                            </Button>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
